import _sortBy from 'lodash.sortby';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { or, readOnly } from '@ember/object/computed';
import Ember from 'ember';
import Controller, { inject as controller } from '@ember/controller';
import { timeout } from 'ember-concurrency';
import { task } from 'ember-concurrency';
import config from 'cio/config/environment';
import ImportModel, { IMPORT_TYPES, ImportState } from 'cio/models/import';
import { getDefaultSegmentMembershipRange } from 'cio/utils/metrics/segment-membership';
import AuthService from 'cio/services/auth';
import ApiService from 'cio/services/api';
import RouterService from '@ember/routing/router-service';
import FeaturesService from 'ember-feature-flags/services/features';
import DS from 'ember-data';
import SegmentPeopleController from 'cio/pods/segments/segment/people/controller';
import SegmentModel from 'cio/models/segment';
import SqlImportRowModel from 'cio/models/sql-import-row';

const SEGMENT_RELOAD_INTERVAL = config.APP.segmentReloadInterval;

export default class SegmentController extends Controller {
  @service auth!: AuthService;
  @service api!: ApiService;
  @service router!: RouterService;
  @service features!: FeaturesService;
  @service store!: DS.Store;

  declare model: SegmentModel;

  @controller('segments.segment.people') segmentPeopleController!: SegmentPeopleController;

  hasImportsInProgress = false;

  segmentMembershipChangeExportRange = getDefaultSegmentMembershipRange();

  @readOnly('fetchPeopleCount.last.value') peopleCount!: number;
  @readOnly('fetchPeopleCount.isRunning') isLoadingPeopleCount!: boolean;
  @readOnly('fetchImports.last.value') imports!: (SqlImportRowModel | ImportModel)[];
  @or('model.isBuilding', 'pollForBuildFinish.isRunning') isBuildingSegment!: boolean;

  @computed('router.currentRouteName')
  get isImportingCSV() {
    return this.router.isActive('segments.segment.imports');
  }

  pollForBuildFinish = task(async (addTimeoutAfterBuildFinished = false) => {
    await timeout(SEGMENT_RELOAD_INTERVAL);
    try {
      await this.model.reload();

      if (this.model.isBuilding) {
        if (!Ember.testing) {
          this.pollForBuildFinish.perform();
        }
      } else {
        this.fetchPeopleCount.perform(addTimeoutAfterBuildFinished);
      }
    } catch {
      // ignore
    }
  });

  fetchImports = task({ drop: true }, async () => {
    let { model } = this;

    let [sqlImports, csvImports] = await Promise.all([
      this.store.query('sql-import-row', { segment_id: model.id }),
      this.store.query('import', { type: IMPORT_TYPES.segment, segment_id: model.id }),
    ]);

    // eslint-disable-next-line ember/no-array-prototype-extensions
    return _sortBy([...sqlImports.toArray(), ...csvImports.toArray()], ['createdAt']);
  });

  fetchPeopleCount = task({ drop: true }, async (addTimeout = false) => {
    if (addTimeout === true) {
      await timeout(SEGMENT_RELOAD_INTERVAL);
    }

    return await this.model.loadPeopleCount();
  });

  fetchAndPollForImports = task(async () => {
    let records = await this.fetchImports.perform();

    let hasImportsInProgress = records.some((record) => 'state' in record && record.state === ImportState.Importing);

    if (this.hasImportsInProgress && !hasImportsInProgress) {
      this._didUpdateSegment();
    }

    this.set('hasImportsInProgress', hasImportsInProgress);

    if (!Ember.testing && hasImportsInProgress) {
      await timeout(config.APP.longDelay);
      await this.fetchAndPollForImports.perform();
    }

    return records;
  });

  _didUpdateSegment() {
    this.send('refreshModel');
  }

  @action
  didUpdateSegment() {
    this._didUpdateSegment();
  }

  @action
  reloadSegmentAndPoll() {
    this.pollForBuildFinish.perform(true);

    if (this.router.currentRouteName === 'segments.segment.people') {
      this.segmentPeopleController.pollForBuildFinish?.perform(true);
    }
  }
}

declare module '@ember/controller' {
  interface Registry {
    'segments.segment': SegmentController;
  }
}
